import classNames from "classnames";
import CiroModal from "../shared/CiroModal";
import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { gql, useMutation } from "@apollo/client";
import {
  AutopilotPreviewRunAutopilotModal_RunAutopilotMutation,
  AutopilotPreviewRunAutopilotModal_RunAutopilotMutationVariables,
} from "../../__generated__/graphql";
import CiroTextInput from "../shared/CiroTextInput";
import { useCallback, useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CiroSpinner from "../shared/CiroSpinner";
import { TestContext } from "yup";
import NavigationContext from "../../contexts/NavigationContext";
import { useAutopilotContactCountValidation } from "../../hooks/useAutopilotContactCountValidation";
import MonthlyContactsRemaining from "../shared/MonthlyContactsRemaining";
import { PRICING_PAGE_LINK } from "../../utils/consts";
import CiroLink from "../shared/CiroLink";
import AutopilotRunSkipContactsContainer from "../automation/shared/AutopilotRunSkipContactsContainer";

const AutopilotPreviewRunAutopilotModal_RunAutopilot = gql`
  mutation AutopilotPreviewRunAutopilotModal_RunAutopilot(
    $maxContacts: Int!
    $autopilotCuratedSearchId: Int!
    $skipContactsOptions: SkipContactsOptionsInput!
  ) {
    autopilotStartInternalSession(
      maxContacts: $maxContacts
      autopilotCuratedSearchId: $autopilotCuratedSearchId
      skipContactsOptions: $skipContactsOptions
    ) {
      success
      message
      session {
        id
      }
    }
  }
`;

const AutopilotPreviewRunAutopilotModal = ({
  isOpen,
  onClose,
  curatedSearchId,
  totalContacts,
  isMultisearch,
  onStartedAutopilot,
}: {
  isOpen: boolean;
  onClose: () => void;
  curatedSearchId: number;
  totalContacts: number;
  isMultisearch: boolean;
  onStartedAutopilot: () => void;
}) => {
  const AutopilotPreviewRunAutopilotModalSchema = yup.object({
    maxContactsToSave: yup
      .number()
      .typeError("Please enter a number")
      .required("Please enter a number")
      .min(1, "Please enter a number greater than 0")
      .test("contactLimits", "", function (this: TestContext, value) {
        if (!value) return true;

        // Check against expected contacts in search (for non-multisearch)
        if (!isMultisearch && value > totalContacts) {
          return this.createError({
            message: `There are only an expected ${totalContacts} contacts in this search`,
          });
        }

        // Use the shared validation logic
        const validation = validateContactCount(value);
        if (validation.error) {
          return this.createError({
            message: validation.error,
          });
        }

        return true;
      }),
  });

  const navigate = useNavigate();
  const { autopilotContactLimitDetails } = useContext(NavigationContext);
  const { validateContactCount } = useAutopilotContactCountValidation({
    totalProfiles: totalContacts,
  });

  const [runAutopilot, { loading: runAutopilotLoading }] = useMutation<
    AutopilotPreviewRunAutopilotModal_RunAutopilotMutation,
    AutopilotPreviewRunAutopilotModal_RunAutopilotMutationVariables
  >(AutopilotPreviewRunAutopilotModal_RunAutopilot);

  const {
    handleSubmit,
    watch,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AutopilotPreviewRunAutopilotModalSchema),
    defaultValues: {
      maxContactsToSave: Math.min(30, totalContacts),
    },
    mode: "onChange", // Enable real-time validation
  });

  const maxContactsToSave = watch("maxContactsToSave");

  // Real-time validation state
  const [realTimeError, setRealTimeError] = useState<string | null>(null);
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);

  // Validate input in real-time
  useEffect(() => {
    if (!maxContactsToSave) {
      setRealTimeError(null);
      setUpgradeMessage(null);
      return;
    }
    // Check against expected contacts in search (for non-multisearch)
    if (!isMultisearch && maxContactsToSave > totalContacts) {
      setRealTimeError(
        `There are only an expected ${totalContacts} contacts in this search`,
      );
      return;
    }

    // Use the shared validation logic
    const validation = validateContactCount(maxContactsToSave);
    setRealTimeError(validation.error);
    setUpgradeMessage(validation.upgradeMessage);
  }, [maxContactsToSave, validateContactCount, isMultisearch, totalContacts]);

  // Determine if form is valid
  const isFormValid = !realTimeError && maxContactsToSave > 0;

  const [skipContactsYouveAlreadySeen, setSkipContactsYouveAlreadySeen] =
    useState(false);

  const onSubmit = useCallback(
    (data: { maxContactsToSave: number }) => {
      runAutopilot({
        variables: {
          maxContacts: data.maxContactsToSave,
          autopilotCuratedSearchId: curatedSearchId,
          skipContactsOptions: {
            skipContactsYouveAlreadySeen,
          },
        },
        onCompleted: (data) => {
          if (
            data.autopilotStartInternalSession.success &&
            data.autopilotStartInternalSession.session?.id
          ) {
            navigate(
              `/autopilot/session/${data.autopilotStartInternalSession.session.id}`,
            );
            toast.success("Autopilot started");
            onStartedAutopilot();
            onClose();
          }
        },
        onError: (error) => {
          console.error(error);
          toast.error("Error starting autopilot");
        },
      });
    },
    [
      curatedSearchId,
      navigate,
      runAutopilot,
      onClose,
      onStartedAutopilot,
      skipContactsYouveAlreadySeen,
    ],
  );

  return (
    <CiroModal isOpen={isOpen} onClose={onClose}>
      {/* Modal Header */}
      <div
        className={classNames(
          "flex",
          "justify-between",
          "items-center",
          "mb-3",
        )}
      >
        <h2 className={classNames("text-xl", "font-semibold", "text-gray-900")}>
          Run Autopilot
        </h2>
        <CiroButton
          analyticsField="Close run autopilot modal"
          style={CiroButtonStyleEnum.UNSTYLED}
          onClick={onClose}
          customClassName={classNames(
            "p-1",
            "hover:bg-gray-100",
            "rounded-md",
            "transition-colors",
          )}
        >
          <XMarkIcon className={classNames("w-5", "h-5", "text-gray-400")} />
        </CiroButton>
      </div>
      {/* Modal Content */}
      <div className={classNames("space-y-4")}>
        {/* Input Section */}
        <div className={classNames("rounded-lg", "flex", "flex-col", "gap-4")}>
          <span>How many contacts would you like to analyze?</span>
          {/* Monthly Limits Section */}
          <MonthlyContactsRemaining
            autopilotContactLimitDetails={autopilotContactLimitDetails}
          />
          <CiroTextInput
            type="number"
            register={register("maxContactsToSave")}
            error={realTimeError || errors.maxContactsToSave?.message}
          />
          {upgradeMessage && (
            <div className={classNames("mt-2", "text-gray-600", "text-sm")}>
              <CiroLink unstyled href={PRICING_PAGE_LINK}>
                <span>{upgradeMessage}</span>
                <span className={classNames("ml-1", "text-orange-400")}>→</span>
              </CiroLink>
            </div>
          )}
        </div>

        <AutopilotRunSkipContactsContainer
          skipContactsYouveAlreadySeen={skipContactsYouveAlreadySeen}
          setSkipContactsYouveAlreadySeen={setSkipContactsYouveAlreadySeen}
        />

        {/* Action Button */}
        <div className={classNames("pt-2")}>
          <CiroButton
            customClassName={classNames(
              "w-full",
              "h-12",
              "text-base",
              "font-medium",
            )}
            analyticsField="Start autopilot from modal"
            analyticsProps={{
              maxContactsToSave,
            }}
            style={CiroButtonStyleEnum.LOUD}
            onClick={handleSubmit(onSubmit)}
            disabled={runAutopilotLoading || !isFormValid}
          >
            {runAutopilotLoading ? (
              <div
                className={classNames("flex", "items-center", "justify-center")}
              >
                <CiroSpinner loading={true} />
                <span className={classNames("ml-2")}>Starting...</span>
              </div>
            ) : (
              "Start Autopilot"
            )}
          </CiroButton>
        </div>
      </div>
    </CiroModal>
  );
};

export default AutopilotPreviewRunAutopilotModal;
